'use client';

import { type DragEvent, type ReactNode, useState } from 'react';
import { cn } from '../lib/cn';
import { maxAttachmentsPerDaemonTrigger, type PendingAttachmentDraft } from '../types/attachment';
import { useAttachmentDraftUpload } from './AttachmentDraftUpload';

type AttachmentDropZoneProps = {
  drafts: PendingAttachmentDraft[];
  onChange: (drafts: PendingAttachmentDraft[]) => void;
  children: ReactNode;
  className?: string;
  activeClassName?: string;
  max?: number;
  disabled?: boolean;
};

const hasDraggedFiles = (event: DragEvent<HTMLElement>) => Array.from(event.dataTransfer.types).includes('Files');

export function AttachmentDropZone({
  drafts,
  onChange,
  children,
  className,
  activeClassName,
  max = maxAttachmentsPerDaemonTrigger,
  disabled = false,
}: AttachmentDropZoneProps) {
  const [isDragActive, setIsDragActive] = useState(false);
  const { addFiles, uploadingCount } = useAttachmentDraftUpload({
    drafts,
    onChange,
    max,
    disabled,
  });
  const isDropDisabled = disabled || uploadingCount > 0;

  const handleDragEnter = (event: DragEvent<HTMLDivElement>) => {
    if (!hasDraggedFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    if (!isDropDisabled) setIsDragActive(true);
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    if (!hasDraggedFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = isDropDisabled ? 'none' : 'copy';
    if (!isDropDisabled) setIsDragActive(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    if (!hasDraggedFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
      setIsDragActive(false);
    }
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    if (!hasDraggedFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(false);
    if (isDropDisabled) return;
    void addFiles(event.dataTransfer.files);
  };

  return (
    <div
      className={cn(className, isDragActive && activeClassName)}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {children}
    </div>
  );
}
